import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { TFunction } from 'i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Save, Upload } from 'lucide-react';
import { toast } from 'sonner';

import {
  apiAdminshopsIdPatch,
  apiAdminshopWorkspacesIdPatch,
  apiMediasPost,
  getApiShopsGetCollectionQueryKey,
  getApiShopsIdGetQueryKey
} from '@r/api/generated/client.ts';
import type {
  ShopShopUpdateJsonMergePatchModel,
  ShopShopReadModel,
  ShopWorkspaceShopReadModel,
  ShopWorkspaceShopWorkspaceUpdateJsonMergePatchModel
} from '@r/api/generated/client.schemas.ts';
import {
  toUtcEndOfDay,
  toUtcStartOfDay,
  fromDateInputValue,
  getStartOfToday,
  toDateInputString
} from '@r/utils/date-utils.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@ui/form.tsx';
import { Input } from '@ui/input.tsx';
import { Textarea } from '@ui/textarea.tsx';
import { Button } from '@ui/button.tsx';
import { Switch } from '@ui/switch.tsx';
import { NumberInput } from '@ui/number-input.tsx';
import { Separator } from '@ui/separator.tsx';
import { DatePicker } from '@ui/date-picker.tsx';

/** Construit le schéma de base (sans règles inter-champs) — utilisé aussi par `FormLabel` pour le required-marker. */
function buildCustomShopUpdateBaseSchema(t: TFunction) {
  // Obligatoire pour les boutiques custom (contrainte backend conditionnelle sur `type`,
  // non exprimée dans le schéma généré qui reste `.nullish()` toutes boutiques confondues).
  const requiredEmail = z
    .string()
    .trim()
    .min(1, t('admin.shops.form.email.required'))
    .email(t('admin.shops.form.email.invalid'))
    .max(255, t('admin.shops.form.email.max'));

  return z.object({
    displayName: z
      .string()
      .trim()
      .min(1, t('admin.shops.form.display.name.required'))
      .max(100, t('admin.shops.form.display.name.max')),
    description: z
      .string()
      .trim()
      .min(1, t('admin.shops.form.description.required'))
      .max(255, t('admin.shops.form.description.max')),
    operationalContact: requiredEmail,
    technicalContact: requiredEmail,
    logoFile: z.custom<File | undefined>(
      (value) => value === undefined || (value instanceof File && value.type.startsWith('image/')),
      t('admin.shops.form.file.image.invalid')
    ),
    bannerFile: z.custom<File | undefined>(
      (value) => value === undefined || (value instanceof File && value.type.startsWith('image/')),
      t('admin.shops.form.file.image.invalid')
    ),
    shopOpeningDate: z.string().optional(),
    shopClosingDate: z.string().optional(),
    minPoint: z.coerce
      .number({ invalid_type_error: t('admin.shops.form.min.point.invalid') })
      .int(t('admin.shops.form.min.point.integer'))
      .min(0, t('admin.shops.form.min.point.min')),
    isActive: z.boolean()
  });
}

/** Construit les règles de validation de l'édition avec leurs messages traduits. */
function buildCustomShopUpdateSchema(t: TFunction) {
  return buildCustomShopUpdateBaseSchema(t).superRefine((values, context) => {
    if (
      values.shopOpeningDate &&
      values.shopClosingDate &&
      values.shopClosingDate <= values.shopOpeningDate
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['shopClosingDate'],
        message: t('admin.shops.form.closing.date.after.opening')
      });
    }
  });
}

type CustomShopUpdateValues = z.infer<ReturnType<typeof buildCustomShopUpdateSchema>>;

type UpdateCustomShopFormProps = Omit<ShopShopReadModel, 'shopWorkspace'> & {
  shopWorkspace: ShopWorkspaceShopReadModel;
  onLogoPreviewChange?: (preview?: string) => void;
  onSuccess?: () => void;
};

function buildTechnicalName(displayName: string): string {
  const slug = displayName
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

  return (slug || displayName.trim()).slice(0, 100);
}

function dateToInputValue(value?: string | null): string {
  return value?.slice(0, 10) ?? '';
}

/**
 * Produit une URL temporaire pour prévisualiser un fichier local et la libère
 * dès que le fichier change ou que le composant est démonté.
 */
function useFilePreview(file: File | undefined, fallback?: string | null): string | undefined {
  const [preview, setPreview] = useState<string | undefined>(fallback ?? undefined);

  useEffect(() => {
    if (!file) {
      setPreview(fallback ?? undefined);
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setPreview(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [fallback, file]);

  return preview;
}

/**
 * Édite séparément les données propres à la boutique et les règles portées par
 * son workspace, en n'envoyant à chaque endpoint que les champs modifiés.
 */
export function UpdateCustomShopForm(shop: UpdateCustomShopFormProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const showOrvalErrorToast = useOrvalErrorToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { shopWorkspace } = shop;
  const isActivable = shopWorkspace.isActivable !== false;
  const baseSchema = useMemo(() => buildCustomShopUpdateBaseSchema(t), [t]);
  const formSchema = useMemo(() => buildCustomShopUpdateSchema(t), [t]);

  const form = useForm<CustomShopUpdateValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      displayName: shop.displayName,
      description: shop.description,
      operationalContact: shop.operationalContact ?? '',
      technicalContact: shop.technicalContact ?? '',
      logoFile: undefined,
      bannerFile: undefined,
      shopOpeningDate: dateToInputValue(shopWorkspace.shopOpeningDate),
      shopClosingDate: dateToInputValue(shopWorkspace.shopClosingDate),
      minPoint: shopWorkspace.minPoint ?? 0,
      isActive: shopWorkspace.isActive
    },
    mode: 'onChange'
  });

  const logoPreview = useFilePreview(form.watch('logoFile'), shop.logo?.pathUri);
  const bannerPreview = useFilePreview(form.watch('bannerFile'), shop.banner?.pathUri);

  useEffect(() => {
    shop.onLogoPreviewChange?.(logoPreview);

    return () => shop.onLogoPreviewChange?.(undefined);
  }, [logoPreview, shop.onLogoPreviewChange]);

  const uploadMedia = async (file: File): Promise<string> => {
    const media = await apiMediasPost({ file, type: 'layout' });
    if (!media.iri) throw new Error('Missing media IRI');
    return media.iri;
  };

  const onSubmit = async (values: CustomShopUpdateValues) => {
    setIsSubmitting(true);

    try {
      let hasUpdates = false;
      const dirtyFields = form.formState.dirtyFields;
      // Les nouveaux médias sont téléversés avant de construire le patch qui référence leurs IRI.
      const [logo, banner] = await Promise.all([
        values.logoFile ? uploadMedia(values.logoFile) : Promise.resolve(undefined),
        values.bannerFile ? uploadMedia(values.bannerFile) : Promise.resolve(undefined)
      ]);

      // JSON Merge Patch : omettre un champ permet de conserver sa valeur côté serveur.
      const shopPayload: ShopShopUpdateJsonMergePatchModel = {
        ...(dirtyFields.displayName
          ? {
              name: buildTechnicalName(values.displayName),
              displayName: values.displayName.trim()
            }
          : {}),
        ...(dirtyFields.description ? { description: values.description.trim() } : {}),
        ...(dirtyFields.operationalContact
          ? { operationalContact: values.operationalContact.trim() }
          : {}),
        ...(dirtyFields.technicalContact
          ? { technicalContact: values.technicalContact.trim() }
          : {}),
        ...(logo ? { logo } : {}),
        ...(banner ? { banner } : {})
      };

      if (Object.keys(shopPayload).length > 0) {
        await apiAdminshopsIdPatch(String(shop.id), shopPayload);
        hasUpdates = true;
      }

      if (isActivable) {
        // Les paramètres d'ouverture appartiennent au workspace, pas à la boutique elle-même.
        const workspacePayload: ShopWorkspaceShopWorkspaceUpdateJsonMergePatchModel = {
          ...(dirtyFields.shopOpeningDate
            ? {
                shopOpeningDate: (() => {
                  const openingDate = fromDateInputValue(values.shopOpeningDate);
                  return openingDate ? toUtcStartOfDay(openingDate).toISOString() : null;
                })()
              }
            : {}),
          ...(dirtyFields.shopClosingDate
            ? {
                shopClosingDate: (() => {
                  const closingDate = fromDateInputValue(values.shopClosingDate);
                  return closingDate ? toUtcEndOfDay(closingDate).toISOString() : null;
                })()
              }
            : {}),
          ...(dirtyFields.minPoint ? { minPoint: values.minPoint } : {}),
          ...(dirtyFields.isActive ? { isActive: values.isActive } : {})
        };

        if (Object.keys(workspacePayload).length > 0) {
          await apiAdminshopWorkspacesIdPatch(String(shopWorkspace.id), workspacePayload);
          hasUpdates = true;
        }
      }

      if (!hasUpdates) return;

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: getApiShopsIdGetQueryKey(String(shop.id)) }),
        queryClient.invalidateQueries({ queryKey: getApiShopsGetCollectionQueryKey() })
      ]);

      toast.success(t('form.shop.success', { shopName: values.displayName }));
      form.reset({
        ...values,
        logoFile: undefined,
        bannerFile: undefined
      });
      shop.onSuccess?.();
    } catch (error) {
      showOrvalErrorToast(error, t('form.shop.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form} schema={baseSchema}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <section className="space-y-6">
          <h3 className="text-sm font-medium text-muted-foreground">
            {t('admin.shops.update.form.section.personalization')}
          </h3>
          <Separator />

          <FormField
            control={form.control}
            name="displayName"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('admin.shops.update.form.display.name.label')}</FormLabel>
                <FormControl>
                  <Input maxLength={100} error={!!fieldState.error} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('admin.shops.update.form.description.label')}</FormLabel>
                <FormControl>
                  <Textarea rows={3} maxLength={255} error={!!fieldState.error} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <FormField
              control={form.control}
              name="logoFile"
              render={({ field }) => (
                <FormItem>
                  <FormLabel htmlFor="update-custom-shop-logo">
                    {t('admin.shops.update.form.logo.label')}
                  </FormLabel>
                  <FormControl>
                    <label className="flex min-h-28 cursor-pointer items-center gap-4 rounded-md border border-dashed bg-muted/20 p-4">
                      {logoPreview && (
                        <img
                          src={logoPreview}
                          alt=""
                          className="size-16 rounded-full object-cover"
                        />
                      )}
                      <Upload className="size-4 shrink-0" />
                      <span className="break-all text-sm">
                        {field.value?.name ?? t('admin.shops.update.form.logo.replace')}
                      </span>
                      <input
                        id="update-custom-shop-logo"
                        className="sr-only"
                        type="file"
                        accept="image/*"
                        onChange={(event) => field.onChange(event.target.files?.[0])}
                      />
                    </label>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="bannerFile"
              render={({ field }) => (
                <FormItem>
                  <FormLabel htmlFor="update-custom-shop-banner">
                    {t('admin.shops.update.form.banner.label')}
                  </FormLabel>
                  <FormControl>
                    <label className="relative flex min-h-28 cursor-pointer items-center justify-center overflow-hidden rounded-md border border-dashed bg-muted/20 p-4">
                      {bannerPreview && (
                        <img
                          src={bannerPreview}
                          alt=""
                          className="absolute inset-0 size-full object-cover opacity-40"
                        />
                      )}
                      <span className="relative flex items-center gap-3 break-all text-sm">
                        <Upload className="size-4 shrink-0" />
                        {field.value?.name ?? t('admin.shops.update.form.banner.replace')}
                      </span>
                      <input
                        id="update-custom-shop-banner"
                        className="sr-only"
                        type="file"
                        accept="image/*"
                        onChange={(event) => field.onChange(event.target.files?.[0])}
                      />
                    </label>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </section>

        <section className="space-y-6">
          <h3 className="text-sm font-medium text-muted-foreground">
            {t('admin.shops.update.form.section.opening')}
          </h3>
          <Separator />

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <FormField
              control={form.control}
              name="shopOpeningDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('label.shop.opening.date')}</FormLabel>
                  <FormControl>
                    <DatePicker
                      value={field.value}
                      onChange={(date) => field.onChange(date ? toDateInputString(date) : '')}
                      disabled={!isActivable}
                      isDateDisabled={(date) => {
                        const closingDate = fromDateInputValue(form.getValues('shopClosingDate'));

                        return date < getStartOfToday() || (!!closingDate && date >= closingDate);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="shopClosingDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('label.shop.closing.date')}</FormLabel>
                  <FormControl>
                    <DatePicker
                      value={field.value}
                      onChange={(date) => field.onChange(date ? toDateInputString(date) : '')}
                      disabled={!isActivable}
                      isDateDisabled={(date) => {
                        const openingDate = fromDateInputValue(form.getValues('shopOpeningDate'));

                        return date < getStartOfToday() || (!!openingDate && date <= openingDate);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <FormField
              control={form.control}
              name="minPoint"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('admin.shops.update.form.min.point.label')}</FormLabel>
                  <FormControl>
                    <NumberInput
                      min={0}
                      value={field.value}
                      disabled={!isActivable}
                      onValueChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('admin.shops.update.form.status.label')}</FormLabel>
                  <div className="flex h-10 items-center gap-3">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        disabled={!isActivable}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <span className="text-sm">
                      {field.value
                        ? t('admin.shops.update.form.status.enabled')
                        : t('admin.shops.update.form.status.disabled')}
                    </span>
                  </div>
                </FormItem>
              )}
            />
          </div>
        </section>

        <section className="space-y-6">
          <h3 className="text-sm font-medium text-muted-foreground">
            {t('admin.shops.update.form.section.contact')}
          </h3>
          <Separator />

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <FormField
              control={form.control}
              name="operationalContact"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>{t('admin.shops.update.form.operational.contact.label')}</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder={t('admin.shops.form.email.placeholder')}
                      error={!!fieldState.error}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="technicalContact"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>{t('admin.shops.update.form.technical.contact.label')}</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder={t('admin.shops.form.email.placeholder')}
                      error={!!fieldState.error}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </section>

        {!isActivable && (
          <p className="text-xs text-muted-foreground">{t('shop.not.activable.message')}</p>
        )}

        <Button loading={isSubmitting} className="w-full" type="submit">
          <Save className="mr-2 size-4" />
          {t('form.save.modifications')}
        </Button>
      </form>
    </Form>
  );
}
